import React, { createContext, useEffect, useState } from "react";

import { grid, media } from "@swa-ui/design-tokens/generated/grid";

import deviceRangeValues from "../defines/deviceRangeValues";
import { getSearchParam } from "../getSearchParam";
import { getWebViewType } from "../getWebViewType";
import { window } from "../window";
import { deviceInfoProviderPropTypes } from "./DeviceInfoProvider.propTypes";
import { DeviceInfoProviderProps } from "./DeviceInfoProvider.types";

const COLUMN_SPACING_VALUES = {
  large: grid.large.gutterSpacing,
  medium: grid.medium.gutterSpacing,
  small: grid.small.gutterSpacing,
  xlarge: grid.xlarge.gutterSpacing,
};
const MEDIA_WIDTH_LIMITS_VALUES = {
  large: { max: media.large.max, min: media.large.min },
  medium: { max: media.medium.max, min: media.medium.min },
  small: { max: media.small.max, min: media.small.min },
  xlarge: { min: media.xlarge.min },
};
const NUMBER_COLUMNS_VALUES = {
  large: grid.large.numCols,
  medium: grid.medium.numCols,
  small: grid.small.numCols,
  xlarge: grid.xlarge.numCols,
};
const PADDING_VALUES = {
  large: grid.large.paddingHorizontal,
  medium: grid.medium.paddingHorizontal,
  small: grid.small.paddingHorizontal,
  xlarge: grid.xlarge.paddingHorizontal,
};
const ROW_SPACING_VALUES = {
  large: grid.large.rowSpacing,
  medium: grid.medium.rowSpacing,
  small: grid.small.rowSpacing,
  xlarge: grid.xlarge.rowSpacing,
};
const MEDIA_QUERIES = [
  {
    query: `(max-width: ${deviceRangeValues.smallMax})`,
    size: "small",
  },
  {
    query: `(max-width: ${deviceRangeValues.mediumMax}) and (min-width: ${deviceRangeValues.mediumMin})`,
    size: "medium",
  },
  {
    query: `(max-width: ${deviceRangeValues.largeMax}) and (min-width: ${deviceRangeValues.largeMin})`,
    size: "large",
  },
  {
    query: `(min-width: ${deviceRangeValues.xlargeMin})`,
    size: "xlarge",
  },
];

type Watcher = {
  listener: (event: MediaQueryListEvent) => void;
  query: MediaQueryList;
  size: string;
};

function getActiveScreenSize(watchers: Watcher[]): string {
  return watchers.find((watcher) => watcher.query?.matches)?.size ?? "small";
}

export const DeviceInfoContext = createContext<Record<string, unknown> | undefined>(undefined);

/** Provider used to query and expose information about the current device. */

export const DeviceInfoProvider = (props: DeviceInfoProviderProps) => {
  const { children, darkModeEnabled = false } = props;
  const [screenSize, setScreenSize] = useState(() => getActiveScreenSize(getWatchers()));
  const orientationQuery = window.matchMedia?.("(orientation: portrait)");
  const [isLandscape, setIsLandscape] = useState(!orientationQuery?.matches);
  const [isPortrait, setIsPortrait] = useState(orientationQuery?.matches);
  const [isDarkMode, setIsDarkMode] = useState(
    darkModeEnabled ? getSystemDarkModePreference()?.matches : false
  );
  const [isPrefersReducedMotion, setIsPrefersReducedMotion] = useState(
    window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false
  );
  const isPhysicalKeyboardPresent = window.matchMedia?.(
    "(hover: hover) and (pointer: fine)"
  )?.matches;
  const isTouchDevice = window.matchMedia?.("(hover: none), (pointer: coarse)")?.matches;
  const webViewType = getWebViewType();

  useEffect(() => {
    const handleDarkModeChange = (event: MediaQueryListEvent) => {
      setIsDarkMode(event.matches);
    };

    const handleReducedMotionChange = (event: MediaQueryListEvent) => {
      setIsPrefersReducedMotion(event.matches);
    };

    const reducedMotionQuery = window.matchMedia?.("(prefers-reduced-motion: reduce)");

    if (darkModeEnabled) {
      const mediaQuery = getSystemDarkModePreference();

      mediaQuery.addEventListener("change", handleDarkModeChange);
    }

    if (reducedMotionQuery) {
      reducedMotionQuery.addEventListener("change", handleReducedMotionChange);
    }

    return () => {
      if (darkModeEnabled) {
        const mediaQuery = getSystemDarkModePreference();

        mediaQuery.removeEventListener("change", handleDarkModeChange);
      }

      if (reducedMotionQuery) {
        reducedMotionQuery.removeEventListener("change", handleReducedMotionChange);
      }
    };
  }, []);

  useEffect(() => {
    const watchers = getWatchers();

    orientationQuery.addEventListener
      ? orientationQuery.addEventListener("change", handleChange)
      : orientationQuery.addListener(handleChange); // NOSONAR
    watchers.forEach((watcher) => {
      // NOSONAR
      watcher.query.addEventListener
        ? watcher.query.addEventListener("change", watcher.listener)
        : watcher.query.addListener(watcher.listener); // NOSONAR
    });

    return () => {
      orientationQuery.removeEventListener
        ? orientationQuery.removeEventListener("change", handleChange)
        : orientationQuery.removeListener(handleChange); // NOSONAR
      watchers.forEach((watcher) => {
        // NOSONAR
        watcher.query.removeEventListener
          ? watcher.query.removeEventListener("change", watcher.listener)
          : watcher.query.removeListener(watcher.listener); // NOSONAR
      });
    };
  }, []);

  return (
    <DeviceInfoContext.Provider value={getDeviceInfoContextValue()}>
      {children}
    </DeviceInfoContext.Provider>
  );

  function getSystemDarkModePreference(): MediaQueryList {
    return window.matchMedia?.("(prefers-color-scheme: dark)");
  }

  function handleChange(event: MediaQueryListEvent) {
    setIsLandscape(!event?.matches);
    setIsPortrait(event?.matches);
  }

  function getWatchers(): Watcher[] {
    return MEDIA_QUERIES.map(({ query, size }) => ({
      listener: (event: MediaQueryListEvent) => evaluateSizeWatcher(event, size),
      query: window.matchMedia?.(query),
      size,
    }));
  }

  function evaluateSizeWatcher(event: MediaQueryListEvent, size: string) {
    event?.matches && setScreenSize(size);
  }

  function getEnvironment(): string | undefined {
    const windowUrl = window?.location?.href;
    const isLocalEnv = process.env.NODE_ENV === "development";
    const isTestingEnv = process.env.NODE_ENV === "testing";
    const isProductionEnv = process.env.NODE_ENV === "production";
    const isDevEnvironment = /\.dev\d+\.southwest/.test(windowUrl);
    const isQaEnvironment = /\.qa\d+\.southwest/.test(windowUrl);
    let environment: string | undefined;

    if (isLocalEnv) {
      environment = "local";
    } else if (isTestingEnv) {
      environment = "testing";
    } else if (isDevEnvironment) {
      environment = "dev";
    } else if (isQaEnvironment) {
      environment = "qa";
    } else if (isProductionEnv && !isDevEnvironment && !isQaEnvironment) {
      environment = "production";
    }

    return environment;
  }

  function getDeviceInfoContextValue() {
    return {
      environment: getEnvironment(),
      gridColumnSpacing: getValue(COLUMN_SPACING_VALUES),
      gridNumberColumns: getValue(NUMBER_COLUMNS_VALUES),
      gridPadding: getValue(PADDING_VALUES),
      gridRowSpacing: getValue(ROW_SPACING_VALUES),
      isDarkMode,
      isInIframe: window.self !== window.top && getSearchParam("iframe") === "true",
      isLandscape,
      isLargeOrXlarge: screenSize === "large" || screenSize === "xlarge",
      isPhysicalKeyboardPresent,
      isPortrait,
      isPrefersReducedMotion,
      isSecureWebView: getSearchParam("forceSecureWebView") ?? false,
      isSmallOrMedium: screenSize === "small" || screenSize === "medium",
      isSwaWebView: !!webViewType,
      isTouchDevice,
      isVendorHosted: getIsVendorHosted(),
      mediaQueries: MEDIA_QUERIES,
      mediaWidthLimits: getValue(MEDIA_WIDTH_LIMITS_VALUES),
      screenSize,
      setIsDarkMode,
      webViewType,
    };
  }

  function getValue(values: Record<string, unknown>) {
    return values[screenSize] || 0;
  }

  function getIsVendorHosted(): boolean {
    const { hostname } = window.location;
    const environment = getEnvironment();
    const productionHostRegex = /^(www|espanol|mobile|mobile-offline)\.(southwest|swabiz)\.com$/;

    return (
      process.env.SITE === "external" &&
      environment === "production" &&
      !productionHostRegex.test(hostname)
    );
  }
};

DeviceInfoProvider.displayName = "DeviceInfoProvider";
DeviceInfoContext.displayName = "DeviceInfoContext";
DeviceInfoProvider.propTypes = deviceInfoProviderPropTypes;
